/**
 * E-postadaki bağlantıyla açılan oturumsuz sayfaların (davet kabulü, parola sıfırlama; M7 T2) ortak parçaları.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { acceptInviteInputSchema } from '@castinflow/shared';
import { Alert, Button, Field, Input, buttonClass } from '@castinflow/ui';
import { ApiError } from '../lib/api';
import { tr } from '../i18n/tr';

/**
 * Parola kuralı sunucunun davet kabulü / parola sıfırlama uçlarında uyguladığı şemadan okunur (kayıttaki kuralla aynı
 * sınırlar: `PASSWORD_MIN`–`PASSWORD_MAX`, baştaki/sondaki boşluk reddi). Ayrı bir istemci kuralı yazılmaz.
 */
const passwordRule = acceptInviteInputSchema.shape.password;

/** Sayfa başlığı (rota odağı buraya taşınır) */
export function LinkPageTitle({ children }: { children: ReactNode }) {
  return (
    <h1
      tabIndex={-1}
      className="text-xl font-semibold text-fg focus:outline-none"
      data-page-title=""
    >
      {children}
    </h1>
  );
}

/** Form gösterilmeyen durum (bağlantı eksik / kullanılamıyor / işlem tamam): açıklama + giriş sayfasına dönüş */
export function LinkNotice({
  testId,
  tone,
  title,
  body,
  action,
}: {
  testId: string;
  tone: 'success' | 'warning' | 'danger';
  title: string;
  body: string;
  /** Giriş bağlantısının yanındaki ek eylem (ör. yeni bağlantı iste) */
  action?: ReactNode;
}) {
  return (
    <div className="space-y-4" data-testid={testId}>
      <Alert tone={tone} title={title} announce>
        {body}
      </Alert>
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/login" className={buttonClass('primary')} data-testid={`${testId}-login`}>
          {tr.auth.backToLogin}
        </Link>
        {action}
      </div>
    </div>
  );
}

/**
 * Sunucu hatasının kullanıcıya gösterilecek metni. Sunucunun kendi ileti metni KULLANILMAZ (belirteç ya da teknik ayrıntı
 * taşımaması için yalnız sabit metinler); `null` = belirteç reddedildi (geçersiz / süresi dolmuş / kullanılmış).
 */
export function linkErrorText(e: unknown): string | null {
  if (e instanceof ApiError) {
    if (e.code === 'RATE_LIMITED') return tr.auth.rateLimited;
    if (
      e.status === 403 &&
      (e.details as { reason?: string } | undefined)?.reason === 'ip_not_allowed'
    )
      return tr.auth.ipNotAllowed;
    if (e.status === 401 || e.code === 'UNAUTHENTICATED') return null;
  }
  return tr.auth.linkError;
}

/**
 * Yeni parola + tekrarı: davet kabulü ve parola sıfırlama sayfalarının ortak formu. Parola yalnız gönderim anında çağırana
 * verilir; hata metni parolayı ya da belirteci içermez.
 */
export function NewPasswordForm({
  testId,
  submitLabel,
  busy,
  error,
  onSubmit,
  children,
}: {
  /** Test kimliği öneki: `<önek>-form`, `<önek>-password`, `<önek>-repeat`, `<önek>-submit`, `<önek>-error` */
  testId: string;
  submitLabel: string;
  busy: boolean;
  /** Kullanıcıya gösterilebilir sunucu hatası (`linkErrorText`) */
  error: string | null;
  onSubmit: (password: string) => void;
  /** Formun üstündeki açıklama / notlar */
  children?: ReactNode;
}) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [fieldError, setFieldError] = useState<{ password?: string; repeat?: string }>({});

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = passwordRule.safeParse(password);
    if (!parsed.success) {
      setFieldError({ password: parsed.error.issues[0]?.message ?? tr.auth.passwordHint });
      return;
    }
    if (password !== repeat) {
      setFieldError({ repeat: tr.auth.newPasswordMismatch });
      return;
    }
    setFieldError({});
    onSubmit(password);
  }

  return (
    <form onSubmit={submit} className="space-y-4" noValidate data-testid={`${testId}-form`}>
      {children}
      <Field
        label={tr.auth.newPassword}
        description={tr.auth.passwordHint}
        error={fieldError.password}
        required
      >
        <Input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
          data-testid={`${testId}-password`}
        />
      </Field>
      <Field label={tr.auth.newPasswordRepeat} error={fieldError.repeat} required>
        <Input
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => {
            setRepeat(e.target.value);
          }}
          data-testid={`${testId}-repeat`}
        />
      </Field>
      {error === null ? null : (
        <div data-testid={`${testId}-error`}>
          <Alert tone="danger" title={error} announce />
        </div>
      )}
      <Button
        type="submit"
        variant="primary"
        className="w-full"
        loading={busy}
        data-testid={`${testId}-submit`}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
